import React, { useState, useEffect } from 'react';
import { IoTrash } from 'react-icons/io5';
import { useTestingData } from '../../../../hooks/useTestingData';
import { TestScore } from '../../../../services/testingService';
import { ToastService } from '@/utils/toast';
import './TestingSection.css';

type StandardizedTestType = 'SAT' | 'ACT' | 'BOTH' | 'NEITHER' | '';
type LanguageTestType = 'TOEFL' | 'IELTS' | 'DUOLINGO' | 'PTE' | 'CAMBRIDGE' | '';

interface SectionScore {
  score: string;
  date: string;
}

// Score option generators
const SAT_SECTION_OPTIONS = Array.from({ length: 61 }, (_, i) => 200 + i * 10);
const SAT_TOTAL_OPTIONS = Array.from({ length: 121 }, (_, i) => 400 + i * 10);
const SAT_ESSAY_OPTIONS = Array.from({ length: 19 }, (_, i) => i + 6);
const ACT_OPTIONS = Array.from({ length: 36 }, (_, i) => i + 1);
const ACT_WRITING_OPTIONS = Array.from({ length: 11 }, (_, i) => i + 2);
const TOEFL_SECTION_OPTIONS = Array.from({ length: 31 }, (_, i) => i);
const TOEFL_TOTAL_OPTIONS = Array.from({ length: 121 }, (_, i) => i);
const IELTS_OPTIONS = Array.from({ length: 19 }, (_, i) => parseFloat((i * 0.5).toFixed(1)));
const DUOLINGO_OPTIONS = Array.from({ length: 31 }, (_, i) => 10 + i * 5);
const PTE_OPTIONS = Array.from({ length: 81 }, (_, i) => 10 + i);
const TODAY = new Date().toISOString().split('T')[0];

const getMostRecentDate = (dates: string[]): string => {
  const valid = dates.filter((d) => d && d.trim() !== '');
  if (valid.length === 0) return '';
  return valid.sort().pop() || '';
};

const TestingSection: React.FC = () => {
  const {
    testScores,
    advancedCourses: savedCourses,
    apSubjects,
    ibSubjects,
    satSubjectTests: satSubjectOptions,
    cambridgeSubjects,
    isLoading,
    createTestScore,
    deleteTestScore,
    createAdvancedCoursesBulk,
    deleteAdvancedCourse,
    refreshData,
  } = useTestingData();

  const [standardizedTest, setStandardizedTest] = useState<StandardizedTestType>('');
  const [languageTest, setLanguageTest] = useState<LanguageTestType>('');

  // SAT scores: { score, date } per section
  const [satScores, setSatScores] = useState({
    readingWriting: { score: '', date: '' } as SectionScore,
    math: { score: '', date: '' } as SectionScore,
    total: { score: '', date: '' } as SectionScore,
    essay: { score: '', date: '' } as SectionScore,
  });

  // ACT scores
  const [actScores, setActScores] = useState({
    math: { score: '', date: '' } as SectionScore,
    english: { score: '', date: '' } as SectionScore,
    science: { score: '', date: '' } as SectionScore,
    reading: { score: '', date: '' } as SectionScore,
    composite: { score: '', date: '' } as SectionScore,
  });
  const [submitWritingScore, setSubmitWritingScore] = useState(false);
  const [writingScore, setWritingScore] = useState<SectionScore>({ score: '', date: '' });

  // TOEFL scores
  const [toeflScores, setToeflScores] = useState({
    reading: { score: '', date: '' } as SectionScore,
    writing: { score: '', date: '' } as SectionScore,
    speaking: { score: '', date: '' } as SectionScore,
    listening: { score: '', date: '' } as SectionScore,
    total: { score: '', date: '' } as SectionScore,
  });

  // IELTS scores
  const [ieltsScores, setIeltsScores] = useState({
    reading: { score: '', date: '' } as SectionScore,
    writing: { score: '', date: '' } as SectionScore,
    listening: { score: '', date: '' } as SectionScore,
    speaking: { score: '', date: '' } as SectionScore,
    overall: { score: '', date: '' } as SectionScore,
  });

  // Duolingo scores
  const [duolingoScores, setDuolingoScores] = useState({
    literacy: { score: '', date: '' } as SectionScore,
    comprehension: { score: '', date: '' } as SectionScore,
    conversation: { score: '', date: '' } as SectionScore,
    production: { score: '', date: '' } as SectionScore,
    total: { score: '', date: '' } as SectionScore,
  });

  // PTE scores
  const [pteScores, setPteScores] = useState({
    listening: { score: '', date: '' } as SectionScore,
    reading: { score: '', date: '' } as SectionScore,
    speaking: { score: '', date: '' } as SectionScore,
    writing: { score: '', date: '' } as SectionScore,
    grammar: { score: '', date: '' } as SectionScore,
    oral_fluency: { score: '', date: '' } as SectionScore,
    pronunciation: { score: '', date: '' } as SectionScore,
    spelling: { score: '', date: '' } as SectionScore,
    vocabulary: { score: '', date: '' } as SectionScore,
    written_discourse: { score: '', date: '' } as SectionScore,
  });

  // Cambridge English qualification
  const [cambridgeQualification, setCambridgeQualification] = useState({
    proficiency_level: '',
    exam_date: '',
  });
  const [isSavingAll, setIsSavingAll] = useState(false);

  // Advanced courses — count-based
  const [selectedAdvancedCourses, setSelectedAdvancedCourses] = useState<string[]>([]);
  const [apCount, setApCount] = useState(0);
  const [ibCount, setIbCount] = useState(0);
  const [satSubjectCount, setSatSubjectCount] = useState(0);
  const [cambridgeCount, setCambridgeCount] = useState(0);
  const [apTests, setApTests] = useState<Array<{ subject: string; dateTaken: string; score: string }>>([]);
  const [ibTests, setIbTests] = useState<Array<{ subject: string; dateTaken: string; score: string; level: string }>>([]);
  const [satSubjectTestRows, setSatSubjectTestRows] = useState<Array<{ subject: string; dateTaken: string; score: string }>>([]);
  const [cambridgeTests, setCambridgeTests] = useState<Array<{ subject: string; dateTaken: string; score: string; level: string }>>([]);
  const toPickerValue = (d: string | null): string => d || '';

  useEffect(() => {
    if (isLoading || testScores.length === 0) return;

    const hasSAT = testScores.some((s) => s.test_type === 'SAT');
    const hasACT = testScores.some((s) => s.test_type === 'ACT');
    if (hasSAT && hasACT) setStandardizedTest('BOTH');
    else if (hasSAT) setStandardizedTest('SAT');
    else if (hasACT) setStandardizedTest('ACT');

    const satScore = testScores.find((s) => s.test_type === 'SAT');
    if (satScore) {
      setSatScores({
        readingWriting: { score: String(satScore.scores?.reading_writing ?? ''), date: toPickerValue(satScore.scores?.reading_writing_date) },
        math:           { score: String(satScore.scores?.math ?? ''), date: toPickerValue(satScore.scores?.math_date) },
        total:          { score: String(satScore.scores?.total ?? satScore.total_score ?? ''), date: toPickerValue(satScore.scores?.total_date ?? satScore.test_date) },
        essay:          { score: String(satScore.scores?.essay ?? ''), date: toPickerValue(satScore.scores?.essay_date) },
      });
    }

    const actScore = testScores.find((s) => s.test_type === 'ACT');
    if (actScore) {
      setActScores({
        math:      { score: String(actScore.scores?.math ?? ''), date: toPickerValue(actScore.scores?.math_date) },
        english:   { score: String(actScore.scores?.english ?? ''), date: toPickerValue(actScore.scores?.english_date) },
        science:   { score: String(actScore.scores?.science ?? ''), date: toPickerValue(actScore.scores?.science_date) },
        reading:   { score: String(actScore.scores?.reading ?? ''), date: toPickerValue(actScore.scores?.reading_date) },
        composite: { score: String(actScore.scores?.composite ?? actScore.total_score ?? ''), date: toPickerValue(actScore.scores?.composite_date ?? actScore.test_date) },
      });
      if (actScore.scores?.writing) {
        setSubmitWritingScore(true);
        setWritingScore({ score: String(actScore.scores.writing), date: toPickerValue(actScore.scores?.writing_date) });
      }
    }

    const langTypes = ['TOEFL', 'IELTS', 'DUOLINGO', 'PTE', 'CAMBRIDGE'] as const;
    const langScore = testScores.find((s) => langTypes.includes(s.test_type as typeof langTypes[number]));
    if (langScore) {
      setLanguageTest(langScore.test_type as LanguageTestType);
      if (langScore.test_type === 'TOEFL') {
        setToeflScores({
          reading:   { score: String(langScore.scores?.reading ?? ''), date: toPickerValue(langScore.scores?.reading_date) },
          writing:   { score: String(langScore.scores?.writing ?? ''), date: toPickerValue(langScore.scores?.writing_date) },
          speaking:  { score: String(langScore.scores?.speaking ?? ''), date: toPickerValue(langScore.scores?.speaking_date) },
          listening: { score: String(langScore.scores?.listening ?? ''), date: toPickerValue(langScore.scores?.listening_date) },
          total:     { score: String(langScore.scores?.total ?? langScore.total_score ?? ''), date: toPickerValue(langScore.scores?.total_date ?? langScore.test_date) },
        });
      } else if (langScore.test_type === 'IELTS') {
        setIeltsScores({
          reading:   { score: String(langScore.scores?.reading ?? ''), date: toPickerValue(langScore.scores?.reading_date) },
          writing:   { score: String(langScore.scores?.writing ?? ''), date: toPickerValue(langScore.scores?.writing_date) },
          listening: { score: String(langScore.scores?.listening ?? ''), date: toPickerValue(langScore.scores?.listening_date) },
          speaking:  { score: String(langScore.scores?.speaking ?? ''), date: toPickerValue(langScore.scores?.speaking_date) },
          overall:   { score: String(langScore.scores?.overall ?? langScore.total_score ?? ''), date: toPickerValue(langScore.scores?.overall_date ?? langScore.test_date) },
        });
      } else if (langScore.test_type === 'DUOLINGO') {
        setDuolingoScores({
          literacy:      { score: String(langScore.scores?.literacy ?? ''), date: toPickerValue(langScore.scores?.literacy_date) },
          comprehension: { score: String(langScore.scores?.comprehension ?? ''), date: toPickerValue(langScore.scores?.comprehension_date) },
          conversation:  { score: String(langScore.scores?.conversation ?? ''), date: toPickerValue(langScore.scores?.conversation_date) },
          production:    { score: String(langScore.scores?.production ?? ''), date: toPickerValue(langScore.scores?.production_date) },
          total:         { score: String(langScore.scores?.total ?? langScore.total_score ?? ''), date: toPickerValue(langScore.scores?.total_date ?? langScore.test_date) },
        });
      } else if (langScore.test_type === 'PTE') {
        const fields = ['listening','reading','speaking','writing','grammar','oral_fluency','pronunciation','spelling','vocabulary','written_discourse'] as const;
        setPteScores(Object.fromEntries(
          fields.map((f) => [f, { score: String(langScore.scores?.[f] ?? ''), date: toPickerValue(langScore.scores?.[`${f}_date`]) }])
        ) as typeof pteScores);
      } else if (langScore.test_type === 'CAMBRIDGE') {
        setCambridgeQualification({
          proficiency_level: langScore.scores?.proficiency_level ?? '',
          exam_date: toPickerValue(langScore.test_date),
        });
      }
    }
  }, [isLoading, testScores]);

  useEffect(() => {
    if (isLoading || savedCourses.length === 0) return;

    const types = [...new Set(savedCourses.map((c) => {
      if (c.course_type === 'AP') return 'AP';
      if (c.course_type === 'IB') return 'IB';
      if (c.course_type === 'SAT_SUBJECT') return 'SAT';
      if (c.course_type === 'CAMBRIDGE') return 'Cambridge';
      return null;
    }).filter(Boolean))] as string[];
    setSelectedAdvancedCourses(types);

    const ap = savedCourses.filter((c) => c.course_type === 'AP');
    if (ap.length) { setApCount(ap.length); setApTests(ap.map((c) => ({ subject: c.subject, dateTaken: toPickerValue(c.test_date), score: c.score }))); }

    const ib = savedCourses.filter((c) => c.course_type === 'IB');
    if (ib.length) { setIbCount(ib.length); setIbTests(ib.map((c) => ({ subject: c.subject, dateTaken: toPickerValue(c.test_date), score: c.score, level: c.level ?? '' }))); }

    const sat = savedCourses.filter((c) => c.course_type === 'SAT_SUBJECT');
    if (sat.length) { setSatSubjectCount(sat.length); setSatSubjectTestRows(sat.map((c) => ({ subject: c.subject, dateTaken: toPickerValue(c.test_date), score: c.score }))); }

    const cam = savedCourses.filter((c) => c.course_type === 'CAMBRIDGE');
    if (cam.length) { setCambridgeCount(cam.length); setCambridgeTests(cam.map((c) => ({ subject: c.subject, dateTaken: toPickerValue(c.test_date), score: c.score, level: c.level ?? '' }))); }
  }, [isLoading, savedCourses]);

  // ─── Auto-calculations ─────────────────────────────────────────────────────

  // SAT total = R&W + Math
  useEffect(() => {
    const rw = parseInt(satScores.readingWriting.score) || 0;
    const m = parseInt(satScores.math.score) || 0;
    if (rw > 0 && m > 0) {
      setSatScores((prev) => ({
        ...prev,
        total: {
          score: String(rw + m),
          date: getMostRecentDate([prev.readingWriting.date, prev.math.date]),
        },
      }));
    }
  }, [satScores.readingWriting.score, satScores.math.score]);

  // ACT composite = avg of 4 sections
  useEffect(() => {
    const sections = [actScores.math, actScores.english, actScores.science, actScores.reading];
    const vals = sections.map((s) => parseInt(s.score) || 0).filter((v) => v > 0);
    if (vals.length === 4) {
      setActScores((prev) => ({
        ...prev,
        composite: {
          score: String(Math.round(vals.reduce((a, b) => a + b, 0) / 4)),
          date: getMostRecentDate(sections.map((s) => s.date)),
        },
      }));
    }
  }, [actScores.math.score, actScores.english.score, actScores.science.score, actScores.reading.score]);

  // TOEFL total = sum of 4 sections
  useEffect(() => {
    const sections = [toeflScores.reading, toeflScores.writing, toeflScores.speaking, toeflScores.listening];
    const vals = sections.map((s) => parseInt(s.score) || 0).filter((v) => v > 0);
    if (vals.length === 4) {
      setToeflScores((prev) => ({
        ...prev,
        total: {
          score: String(vals.reduce((a, b) => a + b, 0)),
          date: getMostRecentDate(sections.map((s) => s.date)),
        },
      }));
    }
  }, [toeflScores.reading.score, toeflScores.writing.score, toeflScores.speaking.score, toeflScores.listening.score]);

  // IELTS overall = avg of 4 sections (rounded to nearest 0.5)
  useEffect(() => {
    const sections = [ieltsScores.reading, ieltsScores.writing, ieltsScores.listening, ieltsScores.speaking];
    const vals = sections.map((s) => parseFloat(s.score)).filter((v) => !isNaN(v));
    if (vals.length === 4) {
      const avg = vals.reduce((a, b) => a + b, 0) / 4;
      setIeltsScores((prev) => ({
        ...prev,
        overall: {
          score: (Math.round(avg * 2) / 2).toFixed(1),
          date: getMostRecentDate(sections.map((s) => s.date)),
        },
      }));
    }
  }, [ieltsScores.reading.score, ieltsScores.writing.score, ieltsScores.listening.score, ieltsScores.speaking.score]);

  // Duolingo total = avg of 4 sections
  useEffect(() => {
    const sections = [duolingoScores.literacy, duolingoScores.comprehension, duolingoScores.conversation, duolingoScores.production];
    const vals = sections.map((s) => parseInt(s.score) || 0).filter((v) => v > 0);
    if (vals.length === 4) {
      setDuolingoScores((prev) => ({
        ...prev,
        total: {
          score: String(Math.round(vals.reduce((a, b) => a + b, 0) / 4)),
          date: getMostRecentDate(sections.map((s) => s.date)),
        },
      }));
    }
  }, [duolingoScores.literacy.score, duolingoScores.comprehension.score, duolingoScores.conversation.score, duolingoScores.production.score]);

  // ─── Advanced course handlers ───────────────────────────────────────────────

  const handleAdvancedCourseChange = (courseType: string, checked: boolean) => {
    if (checked) {
      setSelectedAdvancedCourses((prev) => [...prev, courseType]);
    } else {
      setSelectedAdvancedCourses((prev) => prev.filter((c) => c !== courseType));
      switch (courseType) {
        case 'AP': setApCount(0); setApTests([]); break;
        case 'IB': setIbCount(0); setIbTests([]); break;
        case 'SAT': setSatSubjectCount(0); setSatSubjectTestRows([]); break;
        case 'Cambridge': setCambridgeCount(0); setCambridgeTests([]); break;
      }
    }
  };

  const handleCountChange = (courseType: string, count: number) => {
    switch (courseType) {
      case 'AP':
        setApCount(count);
        setApTests(Array.from({ length: count }, (_, i) => apTests[i] || { subject: '', dateTaken: '', score: '' }));
        break;
      case 'IB':
        setIbCount(count);
        setIbTests(Array.from({ length: count }, (_, i) => ibTests[i] || { subject: '', dateTaken: '', score: '', level: '' }));
        break;
      case 'SAT':
        setSatSubjectCount(count);
        setSatSubjectTestRows(Array.from({ length: count }, (_, i) => satSubjectTestRows[i] || { subject: '', dateTaken: '', score: '' }));
        break;
      case 'Cambridge':
        setCambridgeCount(count);
        setCambridgeTests(Array.from({ length: count }, (_, i) => cambridgeTests[i] || { subject: '', dateTaken: '', score: '', level: '' }));
        break;
    }
  };

  const updateTestDetail = (courseType: string, index: number, field: string, value: string) => {
    switch (courseType) {
      case 'AP': {
        const updated = [...apTests];
        updated[index] = { ...updated[index], [field]: value };
        setApTests(updated);
        break;
      }
      case 'IB': {
        const updated = [...ibTests];
        updated[index] = { ...updated[index], [field]: value };
        setIbTests(updated);
        break;
      }
      case 'SAT': {
        const updated = [...satSubjectTestRows];
        updated[index] = { ...updated[index], [field]: value };
        setSatSubjectTestRows(updated);
        break;
      }
      case 'Cambridge': {
        const updated = [...cambridgeTests];
        updated[index] = { ...updated[index], [field]: value };
        if (field === 'level') updated[index].score = '';
        setCambridgeTests(updated);
        break;
      }
    }
  };

  const getCambridgeScoreOptions = (level: string) => {
    if (level === 'Advanced (A-Level)') return ['A*', 'A', 'B', 'C', 'D', 'E'];
    if (level === 'Pre-Advanced (IGCSE)') return ['A*', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];
    return ['A', 'B', 'C', 'D', 'E'];
  };

  // ─── Save handlers ──────────────────────────────────────────────────────────

  const handleSaveSATScores = async () => {
    if (!satScores.total.score) return;
    await createTestScore({
      test_type: 'SAT',
      test_date: satScores.total.date || null,
      scores: {
        total: parseInt(satScores.total.score) || null,
        total_date: satScores.total.date || null,
        reading_writing: parseInt(satScores.readingWriting.score) || null,
        reading_writing_date: satScores.readingWriting.date || null,
        math: parseInt(satScores.math.score) || null,
        math_date: satScores.math.date || null,
        essay: parseInt(satScores.essay.score) || null,
        essay_date: satScores.essay.date || null,
      },
      total_score: parseInt(satScores.total.score) || null,
    });
  };

  const handleSaveACTScores = async () => {
    if (!actScores.composite.score) return;
    await createTestScore({
      test_type: 'ACT',
      test_date: actScores.composite.date || null,
      scores: {
        composite: parseInt(actScores.composite.score) || null,
        composite_date: actScores.composite.date || null,
        math: parseInt(actScores.math.score) || null,
        math_date: actScores.math.date || null,
        english: parseInt(actScores.english.score) || null,
        english_date: actScores.english.date || null,
        science: parseInt(actScores.science.score) || null,
        science_date: actScores.science.date || null,
        reading: parseInt(actScores.reading.score) || null,
        reading_date: actScores.reading.date || null,
        ...(submitWritingScore && writingScore.score
          ? { writing: parseInt(writingScore.score) || null, writing_date: writingScore.date || null }
          : {}),
      },
      total_score: parseInt(actScores.composite.score) || null,
    });
  };

  const handleSaveTOEFLScores = async () => {
    if (!toeflScores.total.score) return;
    await createTestScore({
      test_type: 'TOEFL',
      test_date: toeflScores.total.date || null,
      scores: {
        total: parseInt(toeflScores.total.score) || null,
        total_date: toeflScores.total.date || null,
        reading: parseInt(toeflScores.reading.score) || null,
        reading_date: toeflScores.reading.date || null,
        writing: parseInt(toeflScores.writing.score) || null,
        writing_date: toeflScores.writing.date || null,
        speaking: parseInt(toeflScores.speaking.score) || null,
        speaking_date: toeflScores.speaking.date || null,
        listening: parseInt(toeflScores.listening.score) || null,
        listening_date: toeflScores.listening.date || null,
      },
      total_score: parseInt(toeflScores.total.score) || null,
    });
  };

  const handleSaveIELTSScores = async () => {
    if (!ieltsScores.overall.score) return;
    await createTestScore({
      test_type: 'IELTS',
      test_date: ieltsScores.overall.date || null,
      scores: {
        overall: parseFloat(ieltsScores.overall.score) || null,
        overall_date: ieltsScores.overall.date || null,
        reading: parseFloat(ieltsScores.reading.score) || null,
        reading_date: ieltsScores.reading.date || null,
        writing: parseFloat(ieltsScores.writing.score) || null,
        writing_date: ieltsScores.writing.date || null,
        listening: parseFloat(ieltsScores.listening.score) || null,
        listening_date: ieltsScores.listening.date || null,
        speaking: parseFloat(ieltsScores.speaking.score) || null,
        speaking_date: ieltsScores.speaking.date || null,
      },
      total_score: null,
    });
  };

  const handleSaveDuolingoScores = async () => {
    if (!duolingoScores.total.score) return;
    await createTestScore({
      test_type: 'DUOLINGO',
      test_date: duolingoScores.total.date || null,
      scores: {
        total: parseInt(duolingoScores.total.score) || null,
        total_date: duolingoScores.total.date || null,
        literacy: parseInt(duolingoScores.literacy.score) || null,
        literacy_date: duolingoScores.literacy.date || null,
        comprehension: parseInt(duolingoScores.comprehension.score) || null,
        comprehension_date: duolingoScores.comprehension.date || null,
        conversation: parseInt(duolingoScores.conversation.score) || null,
        conversation_date: duolingoScores.conversation.date || null,
        production: parseInt(duolingoScores.production.score) || null,
        production_date: duolingoScores.production.date || null,
      },
      total_score: parseInt(duolingoScores.total.score) || null,
    });
  };

  const handleSavePTEScores = async () => {
    const hasAny = Object.values(pteScores).some((v) => v.score !== '');
    if (!hasAny) return;
    await createTestScore({
      test_type: 'PTE',
      scores: Object.fromEntries(
        Object.entries(pteScores).flatMap(([k, v]) => [
          [k, parseInt(v.score) || null],
          [`${k}_date`, v.date || null],
        ])
      ),
      total_score: null,
    });
  };

  const handleSaveCambridgeQualification = async () => {
    if (!cambridgeQualification.proficiency_level) return;
    await createTestScore({
      test_type: 'CAMBRIDGE',
      test_date: cambridgeQualification.exam_date || null,
      scores: { proficiency_level: cambridgeQualification.proficiency_level },
      total_score: null,
    });
  };

  const handleSaveAdvancedCourses = async () => {
    const allTests = [
      ...apTests.filter((t) => t.subject).map((t) => ({ course_type: 'AP', subject: t.subject, test_date: t.dateTaken || null, score: t.score, level: null })),
      ...ibTests.filter((t) => t.subject).map((t) => ({ course_type: 'IB', subject: t.subject, test_date: t.dateTaken || null, score: t.score, level: t.level || null })),
      ...satSubjectTestRows.filter((t) => t.subject).map((t) => ({ course_type: 'SAT_SUBJECT', subject: t.subject, test_date: t.dateTaken || null, score: t.score, level: null })),
      ...cambridgeTests.filter((t) => t.subject).map((t) => ({ course_type: 'CAMBRIDGE', subject: t.subject, test_date: t.dateTaken || null, score: t.score, level: t.level || null })),
    ];
    await createAdvancedCoursesBulk(allTests);
  };

  const handleSaveAll = async () => {
    setIsSavingAll(true);
    try {
      if ((standardizedTest === 'SAT' || standardizedTest === 'BOTH') && satScores.total.score) {
        await handleSaveSATScores();
      }
      if ((standardizedTest === 'ACT' || standardizedTest === 'BOTH') && actScores.composite.score) {
        await handleSaveACTScores();
      }
      if (languageTest === 'TOEFL' && toeflScores.total.score) await handleSaveTOEFLScores();
      else if (languageTest === 'IELTS' && ieltsScores.overall.score) await handleSaveIELTSScores();
      else if (languageTest === 'DUOLINGO' && duolingoScores.total.score) await handleSaveDuolingoScores();
      else if (languageTest === 'PTE' && Object.values(pteScores).some((v) => v.score !== '')) await handleSavePTEScores();
      else if (languageTest === 'CAMBRIDGE' && cambridgeQualification.proficiency_level) await handleSaveCambridgeQualification();
      if (hasAdvancedCourseRows) await handleSaveAdvancedCourses();
      await refreshData();
      ToastService.showSuccess('Testing data saved successfully.');
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save. Please try again.';
      ToastService.showError(msg);
    } finally {
      setIsSavingAll(false);
    }
  };

  // ─── Derived ────────────────────────────────────────────────────────────────

  const satTestScores = testScores.filter((s: TestScore) => s.test_type === 'SAT');
  const actTestScores = testScores.filter((s: TestScore) => s.test_type === 'ACT');
  const languageTestScores = testScores.filter((s: TestScore) =>
    ['TOEFL', 'IELTS', 'DUOLINGO', 'PTE', 'CAMBRIDGE'].includes(s.test_type)
  );

  const hasAdvancedCourseRows =
    apTests.some((t) => t.subject) ||
    ibTests.some((t) => t.subject) ||
    satSubjectTestRows.some((t) => t.subject) ||
    cambridgeTests.some((t) => t.subject);

  if (isLoading) return <div className="testing-loading">Loading testing data...</div>;

  // ─── Render helpers ─────────────────────────────────────────────────────────

  const renderAutoOverlay = (value: string) =>
    value ? (
      <div className="testing-auto-overlay">
        <span className="testing-auto-value">{value}</span>
      </div>
    ) : null;

  return (
    <div className="testing-section">

      {/* Did you take the SAT or ACT? */}
      <div className="testing-form-section">
        <label className="testing-label">Did you take the SAT or ACT?</label>
        <div className="testing-radio-group">
          {(['SAT', 'ACT', 'BOTH', 'NEITHER'] as const).map((option) => (
            <label key={option} className="testing-radio-label">
              <input
                type="radio"
                name="standardizedTest"
                value={option}
                checked={standardizedTest === option}
                onChange={(e) => setStandardizedTest(e.target.value as StandardizedTestType)}
                className="testing-radio"
              />
              <span>{option === 'BOTH' ? 'Both' : option === 'NEITHER' ? 'Neither' : option}</span>
            </label>
          ))}
        </div>
      </div>

      {/* ── ACT Scores ─────────────────────────────────────────────────────── */}
      {(standardizedTest === 'ACT' || standardizedTest === 'BOTH') && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">ACT Scores (Highest Scores)</h4>

          {actTestScores.length > 0 && (
            <div className="testing-existing-scores">
              {actTestScores.map((score) => (
                <div key={score.id} className="testing-score-card">
                  <div className="testing-score-info">
                    <span className="testing-score-type">ACT</span>
                    <span className="testing-score-value">Composite: {score.scores?.composite || score.total_score}</span>
                    {score.scores?.math && <span className="testing-score-detail">Math: {score.scores.math}</span>}
                    {score.scores?.english && <span className="testing-score-detail">English: {score.scores.english}</span>}
                    {score.scores?.science && <span className="testing-score-detail">Science: {score.scores.science}</span>}
                    {score.scores?.reading && <span className="testing-score-detail">Reading: {score.scores.reading}</span>}
                  </div>
                  <button className="testing-delete-btn" onClick={() => deleteTestScore(score.id)}><IoTrash /></button>
                </div>
              ))}
            </div>
          )}

          {/* Score dropdowns */}
          <div className="testing-score-inputs">
            <div className="testing-input-row">
              {(['math', 'english', 'science', 'reading'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <select value={actScores[field].score} onChange={(e) => setActScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                    <option value="">Score</option>
                    {ACT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              <div className="testing-input-field">
                <label>Composite <span className="testing-hint">(Auto)</span></label>
                <div className="testing-relative">
                  <select value={actScores.composite.score} onChange={(e) => setActScores((prev) => ({ ...prev, composite: { ...prev.composite, score: e.target.value } }))}>
                    <option value="">Score</option>
                    {ACT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderAutoOverlay(actScores.composite.score)}
                </div>
              </div>
            </div>

            {/* Date row */}
            <div className="testing-input-row">
              {(['math', 'english', 'science', 'reading', 'composite'] as const).map((field) => {
                const isComposite = field === 'composite';
                return (
                  <div key={field} className="testing-input-field">
                    <label>Date{isComposite && <span className="testing-hint"> (Auto-filled)</span>}</label>
                    <input type="date" value={actScores[field].date} max={TODAY} onChange={(e) => setActScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                  </div>
                );
              })}
            </div>

            {/* Writing score */}
            <div className="testing-writing-section">
              <label className="testing-label">Would you like to submit a Writing score?</label>
              <div className="testing-radio-group">
                {[{ label: 'Yes', val: true }, { label: 'No', val: false }].map(({ label, val }) => (
                  <label key={label} className="testing-radio-label">
                    <input type="radio" name="submitWriting" checked={submitWritingScore === val} onChange={() => setSubmitWritingScore(val)} className="testing-radio" />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              {submitWritingScore && (
                <div className="testing-input-row testing-input-row--narrow">
                  <div className="testing-input-field">
                    <label>Writing Score (2-12)</label>
                    <select value={writingScore.score} onChange={(e) => setWritingScore((prev) => ({ ...prev, score: e.target.value }))}>
                      <option value="">Select...</option>
                      {ACT_WRITING_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="testing-input-field">
                    <label>Date</label>
                    <input type="date" value={writingScore.date} max={TODAY} onChange={(e) => setWritingScore((prev) => ({ ...prev, date: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      )}

      {/* ── SAT Scores ─────────────────────────────────────────────────────── */}
      {(standardizedTest === 'SAT' || standardizedTest === 'BOTH') && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">SAT Scores (Highest Scores)</h4>

          {satTestScores.length > 0 && (
            <div className="testing-existing-scores">
              {satTestScores.map((score) => (
                <div key={score.id} className="testing-score-card">
                  <div className="testing-score-info">
                    <span className="testing-score-type">SAT</span>
                    <span className="testing-score-value">Total: {score.scores?.total || score.total_score}</span>
                    {score.scores?.reading_writing && <span className="testing-score-detail">R&W: {score.scores.reading_writing}</span>}
                    {score.scores?.math && <span className="testing-score-detail">Math: {score.scores.math}</span>}
                    {score.scores?.essay && <span className="testing-score-detail">Essay: {score.scores.essay}</span>}
                  </div>
                  <button className="testing-delete-btn" onClick={() => deleteTestScore(score.id)}><IoTrash /></button>
                </div>
              ))}
            </div>
          )}

          <div className="testing-score-inputs">
            <div className="testing-input-row">
              <div className="testing-input-field">
                <label>Reading & Writing</label>
                <select value={satScores.readingWriting.score} onChange={(e) => setSatScores((prev) => ({ ...prev, readingWriting: { ...prev.readingWriting, score: e.target.value } }))}>
                  <option value="">Score</option>
                  {SAT_SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Math</label>
                <select value={satScores.math.score} onChange={(e) => setSatScores((prev) => ({ ...prev, math: { ...prev.math, score: e.target.value } }))}>
                  <option value="">Score</option>
                  {SAT_SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Total <span className="testing-hint">(Auto)</span></label>
                <div className="testing-relative">
                  <select value={satScores.total.score} onChange={(e) => setSatScores((prev) => ({ ...prev, total: { ...prev.total, score: e.target.value } }))}>
                    <option value="">Score</option>
                    {SAT_TOTAL_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderAutoOverlay(satScores.total.score)}
                </div>
              </div>
              <div className="testing-input-field">
                <label>Essay</label>
                <select value={satScores.essay.score} onChange={(e) => setSatScores((prev) => ({ ...prev, essay: { ...prev.essay, score: e.target.value } }))}>
                  <option value="">Score</option>
                  {SAT_ESSAY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Date row */}
            <div className="testing-input-row">
              {(['readingWriting', 'math', 'total', 'essay'] as const).map((field) => {
                const isTotal = field === 'total';
                const labels: Record<string, string> = { readingWriting: 'R&W Date', math: 'Math Date', total: 'Total Date', essay: 'Essay Date' };
                return (
                  <div key={field} className="testing-input-field">
                    <label>{labels[field]}{isTotal && <span className="testing-hint"> (Auto-filled)</span>}</label>
                    <input type="date" value={satScores[field].date} max={TODAY} onChange={(e) => setSatScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      )}

      {/* ── Advanced Courses ─────────────────────────────────────────────── */}
      <div className="testing-form-section">
        <label className="testing-label">
          Advanced courses <span className="testing-hint">(Select all that apply)</span>
        </label>
        <div className="testing-checkbox-group">
          {[
            { value: 'IB', label: 'IB Subject Tests' },
            { value: 'AP', label: 'AP Subject Tests' },
            { value: 'SAT', label: 'SAT Subject Tests' },
            { value: 'Cambridge', label: 'Cambridge Tests' },
          ].map((option) => (
            <label key={option.value} className="testing-checkbox-label">
              <input type="checkbox" className="testing-checkbox" checked={selectedAdvancedCourses.includes(option.value)} onChange={(e) => handleAdvancedCourseChange(option.value, e.target.checked)} />
              <span>{option.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Saved advanced courses */}
      {savedCourses.length > 0 && (
        <div className="testing-advanced-courses-list">
          <h4 className="testing-scores-title">Your Advanced Courses</h4>
          <div className="testing-courses-grid">
            {savedCourses.map((course) => (
              <div key={course.id} className="testing-course-card">
                <div className="testing-course-info">
                  <span className="testing-course-type">{course.course_type}</span>
                  <span className="testing-course-subject">{course.subject}</span>
                  <span className="testing-course-score">Score: {course.score}</span>
                  {course.level && <span className="testing-course-level">Level: {course.level}</span>}
                </div>
                <button className="testing-delete-btn" onClick={() => deleteAdvancedCourse(course.id)}><IoTrash /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AP Details */}
      {selectedAdvancedCourses.includes('AP') && (
        <div className="testing-course-details-section">
          <label className="testing-label">How many AP scores would you like to report?</label>
          <select className="testing-count-select" value={apCount} onChange={(e) => handleCountChange('AP', parseInt(e.target.value))}>
            <option value={0}>Select...</option>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {apCount > 0 && apTests.map((test, i) => (
            <div key={i} className="testing-course-row">
              <div className="testing-input-field">
                <label>Subject</label>
                <select value={test.subject} onChange={(e) => updateTestDetail('AP', i, 'subject', e.target.value)}>
                  <option value="">Select subject...</option>
                  {apSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Date Taken/Planned</label>
                <input type="date" value={test.dateTaken} onChange={(e) => updateTestDetail('AP', i, 'dateTaken', e.target.value)} />
              </div>
              <div className="testing-input-field">
                <label>Score</label>
                <select value={test.score} onChange={(e) => updateTestDetail('AP', i, 'score', e.target.value)}>
                  <option value="">Select score...</option>
                  {['5','4','3','2','1'].map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="I don't know yet">I don't know yet</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* IB Details */}
      {selectedAdvancedCourses.includes('IB') && (
        <div className="testing-course-details-section">
          <label className="testing-label">How many IB scores would you like to report?</label>
          <select className="testing-count-select" value={ibCount} onChange={(e) => handleCountChange('IB', parseInt(e.target.value))}>
            <option value={0}>Select...</option>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {ibCount > 0 && ibTests.map((test, i) => (
            <div key={i} className="testing-course-row testing-course-row--4col">
              <div className="testing-input-field">
                <label>Subject</label>
                <select value={test.subject} onChange={(e) => updateTestDetail('IB', i, 'subject', e.target.value)}>
                  <option value="">Select subject...</option>
                  {ibSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Course Level</label>
                <select value={test.level} onChange={(e) => updateTestDetail('IB', i, 'level', e.target.value)}>
                  <option value="">Select level...</option>
                  <option value="Higher Level (HL)">Higher Level (HL)</option>
                  <option value="Standard Level (SL)">Standard Level (SL)</option>
                </select>
              </div>
              <div className="testing-input-field">
                <label>Date Taken/Planned</label>
                <input type="date" value={test.dateTaken} onChange={(e) => updateTestDetail('IB', i, 'dateTaken', e.target.value)} />
              </div>
              <div className="testing-input-field">
                <label>Score</label>
                <select value={test.score} onChange={(e) => updateTestDetail('IB', i, 'score', e.target.value)}>
                  <option value="">Select score...</option>
                  {['7','6','5','4','3','2','1'].map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="I don't know yet">I don't know yet</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SAT Subject Tests Details */}
      {selectedAdvancedCourses.includes('SAT') && (
        <div className="testing-course-details-section">
          <label className="testing-label">How many SAT Subject Test scores would you like to report?</label>
          <select className="testing-count-select" value={satSubjectCount} onChange={(e) => handleCountChange('SAT', parseInt(e.target.value))}>
            <option value={0}>Select...</option>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {satSubjectCount > 0 && satSubjectTestRows.map((test, i) => (
            <div key={i} className="testing-course-row">
              <div className="testing-input-field">
                <label>Subject</label>
                <select value={test.subject} onChange={(e) => updateTestDetail('SAT', i, 'subject', e.target.value)}>
                  <option value="">Select subject...</option>
                  {satSubjectOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Date Taken/Planned</label>
                <input type="date" value={test.dateTaken} onChange={(e) => updateTestDetail('SAT', i, 'dateTaken', e.target.value)} />
              </div>
              <div className="testing-input-field">
                <label>Score</label>
                <select value={test.score} onChange={(e) => updateTestDetail('SAT', i, 'score', e.target.value)}>
                  <option value="">Select score...</option>
                  {SAT_SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  <option value="I don't know yet">I don't know yet</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Cambridge Advanced Tests Details */}
      {selectedAdvancedCourses.includes('Cambridge') && (
        <div className="testing-course-details-section">
          <label className="testing-label">How many Cambridge Test scores would you like to report?</label>
          <select className="testing-count-select" value={cambridgeCount} onChange={(e) => handleCountChange('Cambridge', parseInt(e.target.value))}>
            <option value={0}>Select...</option>
            {[1,2,3,4,5,6,7,8,9,10].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          {cambridgeCount > 0 && cambridgeTests.map((test, i) => (
            <div key={i} className="testing-course-row testing-course-row--4col">
              <div className="testing-input-field">
                <label>Subject</label>
                <select value={test.subject} onChange={(e) => updateTestDetail('Cambridge', i, 'subject', e.target.value)}>
                  <option value="">Select subject...</option>
                  {cambridgeSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="testing-input-field">
                <label>Course Level</label>
                <select value={test.level} onChange={(e) => updateTestDetail('Cambridge', i, 'level', e.target.value)}>
                  <option value="">Select level...</option>
                  <option value="Advanced (A-Level)">Advanced (A-Level)</option>
                  <option value="Advanced (AS-Level)">Advanced (AS-Level)</option>
                  <option value="Pre-Advanced (IGCSE)">Pre-Advanced (IGCSE)</option>
                </select>
              </div>
              <div className="testing-input-field">
                <label>Expected Test Date</label>
                <input type="date" value={test.dateTaken} onChange={(e) => updateTestDetail('Cambridge', i, 'dateTaken', e.target.value)} />
              </div>
              <div className="testing-input-field">
                <label>Grade</label>
                <select value={test.score} onChange={(e) => updateTestDetail('Cambridge', i, 'score', e.target.value)} disabled={!test.level}>
                  <option value="">Select grade...</option>
                  {test.level && getCambridgeScoreOptions(test.level).map((g) => <option key={g} value={g}>{g}</option>)}
                  <option value="I don't know yet">I don't know yet</option>
                </select>
                {!test.level && <p className="testing-hint-text">Select course level first</p>}
              </div>
            </div>
          ))}
        </div>
      )}


      {/* ── Language Tests ───────────────────────────────────────────────── */}
      <div className="testing-language-section">
        <h4 className="testing-section-title">Language Tests</h4>
        <label className="testing-label">
          Language Test Taken <span className="testing-hint">(only if English is not your first language)</span>
        </label>
        <select className="testing-select" value={languageTest} onChange={(e) => setLanguageTest(e.target.value as LanguageTestType)}>
          <option value="">Select a language test...</option>
          <option value="TOEFL">TOEFL iBT</option>
          <option value="IELTS">IELTS</option>
          <option value="DUOLINGO">Duolingo English Test</option>
          <option value="PTE">PTE Academic</option>
          <option value="CAMBRIDGE">Cambridge English</option>
        </select>
      </div>

      {/* Saved language test scores */}
      {languageTestScores.length > 0 && (
        <div className="testing-existing-scores">
          {languageTestScores.map((score) => (
            <div key={score.id} className="testing-score-card">
              <div className="testing-score-info">
                <span className="testing-score-type">{score.test_type}</span>
                {score.scores?.proficiency_level
                  ? <span className="testing-score-value">{score.scores.proficiency_level}</span>
                  : <span className="testing-score-value">Total: {score.scores?.total || score.scores?.overall || score.total_score}</span>
                }
              </div>
              <button className="testing-delete-btn" onClick={() => deleteTestScore(score.id)}><IoTrash /></button>
            </div>
          ))}
        </div>
      )}

      {/* ── TOEFL ────────────────────────────────────────────────────────── */}
      {languageTest === 'TOEFL' && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">TOEFL iBT Scores (Highest Scores)</h4>
          <div className="testing-score-inputs">
            <div className="testing-input-row">
              {(['reading', 'writing', 'speaking', 'listening'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <select value={toeflScores[field].score} onChange={(e) => setToeflScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                    <option value="">Score</option>
                    {TOEFL_SECTION_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              <div className="testing-input-field">
                <label>Total <span className="testing-hint">(Auto)</span></label>
                <div className="testing-relative">
                  <select value={toeflScores.total.score} onChange={(e) => setToeflScores((prev) => ({ ...prev, total: { ...prev.total, score: e.target.value } }))}>
                    <option value="">Score</option>
                    {TOEFL_TOTAL_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderAutoOverlay(toeflScores.total.score)}
                </div>
              </div>
            </div>
            <div className="testing-input-row">
              {(['reading', 'writing', 'speaking', 'listening', 'total'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>Date{field === 'total' && <span className="testing-hint"> (Auto-filled)</span>}</label>
                  <input type="date" value={toeflScores[field].date} max={TODAY} onChange={(e) => setToeflScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── IELTS ────────────────────────────────────────────────────────── */}
      {languageTest === 'IELTS' && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">IELTS Scores (Highest Scores)</h4>
          <div className="testing-score-inputs">
            <div className="testing-input-row">
              {(['reading', 'writing', 'listening', 'speaking'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <select value={ieltsScores[field].score} onChange={(e) => setIeltsScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                    <option value="">Score</option>
                    {IELTS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              <div className="testing-input-field">
                <label>Overall <span className="testing-hint">(Auto)</span></label>
                <div className="testing-relative">
                  <select value={ieltsScores.overall.score} onChange={(e) => setIeltsScores((prev) => ({ ...prev, overall: { ...prev.overall, score: e.target.value } }))}>
                    <option value="">Score</option>
                    {IELTS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderAutoOverlay(ieltsScores.overall.score)}
                </div>
              </div>
            </div>
            <div className="testing-input-row">
              {(['reading', 'writing', 'listening', 'speaking', 'overall'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>Date{field === 'overall' && <span className="testing-hint"> (Auto-filled)</span>}</label>
                  <input type="date" value={ieltsScores[field].date} max={TODAY} onChange={(e) => setIeltsScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Duolingo ─────────────────────────────────────────────────────── */}
      {languageTest === 'DUOLINGO' && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">Duolingo English Test Scores (Highest Scores)</h4>
          <div className="testing-score-inputs">
            <div className="testing-input-row">
              {(['literacy', 'comprehension', 'conversation', 'production'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                  <select value={duolingoScores[field].score} onChange={(e) => setDuolingoScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                    <option value="">Score</option>
                    {DUOLINGO_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              ))}
              <div className="testing-input-field">
                <label>Overall <span className="testing-hint">(Auto)</span></label>
                <div className="testing-relative">
                  <select value={duolingoScores.total.score} onChange={(e) => setDuolingoScores((prev) => ({ ...prev, total: { ...prev.total, score: e.target.value } }))}>
                    <option value="">Score</option>
                    {DUOLINGO_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {renderAutoOverlay(duolingoScores.total.score)}
                </div>
              </div>
            </div>
            <div className="testing-input-row">
              {(['literacy', 'comprehension', 'conversation', 'production', 'total'] as const).map((field) => (
                <div key={field} className="testing-input-field">
                  <label>Date{field === 'total' && <span className="testing-hint"> (Auto-filled)</span>}</label>
                  <input type="date" value={duolingoScores[field].date} max={TODAY} onChange={(e) => setDuolingoScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── PTE ──────────────────────────────────────────────────────────── */}
      {languageTest === 'PTE' && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">PTE Academic Test Scores (Highest Scores)</h4>
          <div className="testing-score-inputs">
            <div className="testing-pte-group">
              <p className="testing-pte-group-label">Core Skills</p>
              <div className="testing-input-row">
                {(['listening', 'reading', 'speaking', 'writing'] as const).map((field) => (
                  <div key={field} className="testing-input-field">
                    <label>{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                    <select value={pteScores[field].score} onChange={(e) => setPteScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                      <option value="">Score</option>
                      {PTE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="testing-input-row">
                {(['listening', 'reading', 'speaking', 'writing'] as const).map((field) => (
                  <div key={field} className="testing-input-field">
                    <label>Date</label>
                    <input type="date" value={pteScores[field].date} max={TODAY} onChange={(e) => setPteScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                  </div>
                ))}
              </div>
            </div>
            <div className="testing-pte-group">
              <p className="testing-pte-group-label">Enabling Skills</p>
              <div className="testing-input-row">
                {([['grammar','Grammar'],['oral_fluency','Oral Fluency'],['pronunciation','Pronunciation'],['spelling','Spelling'],['vocabulary','Vocabulary'],['written_discourse','Written Discourse']] as [keyof typeof pteScores, string][]).map(([field, label]) => (
                  <div key={field} className="testing-input-field">
                    <label>{label}</label>
                    <select value={pteScores[field].score} onChange={(e) => setPteScores((prev) => ({ ...prev, [field]: { ...prev[field], score: e.target.value } }))}>
                      <option value="">Score</option>
                      {PTE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="testing-input-row">
                {(['grammar','oral_fluency','pronunciation','spelling','vocabulary','written_discourse'] as (keyof typeof pteScores)[]).map((field) => (
                  <div key={field} className="testing-input-field">
                    <label>Date</label>
                    <input type="date" value={pteScores[field].date} max={TODAY} onChange={(e) => setPteScores((prev) => ({ ...prev, [field]: { ...prev[field], date: e.target.value } }))} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Cambridge English ─────────────────────────────────────────────── */}
      {languageTest === 'CAMBRIDGE' && (
        <div className="testing-scores-section">
          <h4 className="testing-scores-title">Cambridge English Qualification</h4>
          <div className="testing-score-inputs">
            <div className="testing-input-row">
              <div className="testing-input-field">
                <label>Proficiency Level</label>
                <select value={cambridgeQualification.proficiency_level} onChange={(e) => setCambridgeQualification((prev) => ({ ...prev, proficiency_level: e.target.value }))}>
                  <option value="">Select Level</option>
                  <option value="C2 Proficient">C2 Proficient</option>
                  <option value="C1 Advanced">C1 Advanced</option>
                  <option value="B2 First">B2 First</option>
                  <option value="B1 Preliminary">B1 Preliminary</option>
                  <option value="A2 Key">A2 Key</option>
                  <option value="A1 Movers">A1 Movers</option>
                  <option value="Pre-A1 Starters">Pre-A1 Starters</option>
                </select>
              </div>
              <div className="testing-input-field">
                <label>Exam Date</label>
                <input type="date" value={cambridgeQualification.exam_date} max={TODAY} onChange={(e) => setCambridgeQualification((prev) => ({ ...prev, exam_date: e.target.value }))} />
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="testing-save-all-section">
        <button
          onClick={handleSaveAll}
          disabled={isSavingAll}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0.625rem 1.5rem',
            background: isSavingAll ? '#93c5fd' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.9375rem',
            fontWeight: 600,
            cursor: isSavingAll ? 'not-allowed' : 'pointer',
          }}
        >
          {isSavingAll ? 'Saving...' : 'Save Testing Data'}
        </button>
      </div>
    </div>
  );
};

export default TestingSection;
